/**
 * Hold the screen awake for as long as a voice call is live.
 *
 * The web half of the native phone-to-ear behaviour — and deliberately ONLY that half. Browsers
 * expose no proximity sensor, so a browser cannot know the phone is at an ear and the screen-off side
 * has no counterpart here (see `native-apps/CLAUDE.md`). What IS portable is the screen HOLD, and
 * without it a web call's display times out mid-conversation.
 *
 * ⚠️ **The sentinel is auto-released whenever the page is hidden**, by the spec — switching tabs,
 * minimizing, locking the phone. Nothing tells you it happened; the lock is simply gone when the user
 * comes back. So the visibility listener is not an optimization, it is the difference between this
 * working and it working exactly once. That is the whole reason this module exists rather than a
 * two-line `useEffect` at the call site.
 *
 * No imports on purpose: this file must stay outside the app's dependency graph so jest can exercise
 * it without dragging common-lib's ESM chain in (see `packages/orion-web/CLAUDE.md`).
 */

/** The slice of `WakeLockSentinel` used here, so a test can hand over a fake. */
export interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener?(type: 'release', listener: () => void): void;
}

/** The slice of `navigator.wakeLock` used here. */
export interface WakeLockApiLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

/** `null` where the API is absent — Safari below 16.4, any non-secure context, SSR. */
export function screenWakeLockApi(): WakeLockApiLike | null {
  if (typeof navigator === 'undefined') return null;
  const api = (navigator as Navigator & { wakeLock?: WakeLockApiLike }).wakeLock;
  return api && typeof api.request === 'function' ? api : null;
}

/**
 * Owns one sentinel across the life of a call, re-taking it every time the page comes back.
 *
 * Every method is safe to call in any order and any number of times: the call surface drives this
 * from React effects, which double-invoke in StrictMode and can race a visibility change.
 */
export class ScreenWakeLockHolder {
  private sentinel: WakeLockSentinelLike | null = null;
  private wanted = false;
  /** Guards against two overlapping `request()` calls leaving an orphaned sentinel nobody releases. */
  private pending: Promise<void> | null = null;

  constructor(
    private readonly api: WakeLockApiLike | null = screenWakeLockApi(),
    private readonly isVisible: () => boolean = () =>
      typeof document === 'undefined' || document.visibilityState === 'visible',
  ) {}

  get isHeld(): boolean {
    return this.sentinel != null && !this.sentinel.released;
  }

  /** Ask for the screen. A refusal is swallowed: a call must never fail over a display hint. */
  async acquire(): Promise<void> {
    this.wanted = true;
    if (!this.api || this.isHeld || this.pending) return;
    // The spec rejects a request from a hidden page, and the visibility listener will retake it when
    // the page returns — so not even attempting is the correct behaviour, not a missed case.
    if (!this.isVisible()) return;
    const attempt = this.api
      .request('screen')
      .then((sentinel) => {
        // Released while we were awaiting: drop it rather than storing a dead sentinel that would
        // make `isHeld` lie and suppress the next re-acquire.
        if (!this.wanted) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        this.sentinel = sentinel;
      })
      .catch(() => undefined)
      .finally(() => {
        this.pending = null;
      });
    this.pending = attempt;
    return attempt;
  }

  /** Give the screen back. Idempotent, and never throws. */
  async release(): Promise<void> {
    this.wanted = false;
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (!sentinel || sentinel.released) return;
    await sentinel.release().catch(() => undefined);
  }

  /**
   * Call on every `visibilitychange`.
   *
   * Coming back is the case that matters: the browser silently dropped the sentinel while we were
   * hidden, so without this a call that survives one tab switch spends the rest of its life letting
   * the display time out.
   */
  async onVisibilityChange(): Promise<void> {
    if (!this.wanted) return;
    if (this.isVisible()) await this.acquire();
  }
}
